import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Tag } from "@/components/Layout";
import MyAssetsStrip from "@/components/MyAssetsStrip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, Bell } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [title, setTitle] = useState(user?.title || "");
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [prefGroups, setPrefGroups] = useState([]);
  const [quietHours, setQuietHours] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get("/notification-preferences").then(({ data }) => {
      setPrefGroups(data.groups || []);
      setQuietHours(!!data.quiet_hours_enabled);
    }).catch(() => {});
  }, [user]);

  if (!user) return null;

  const toggleGroup = async (key, checked) => {
    setPrefGroups((rows) => rows.map((g) => g.key === key ? { ...g, email_enabled: checked } : g));
    try {
      await api.patch("/notification-preferences", { [key]: checked });
    } catch (e) {
      toast.error(formatApiError(e));
      setPrefGroups((rows) => rows.map((g) => g.key === key ? { ...g, email_enabled: !checked } : g));
    }
  };

  const toggleQuietHours = async (checked) => {
    setQuietHours(checked);
    try {
      await api.patch("/notification-preferences", { quiet_hours: checked });
    } catch (e) {
      toast.error(formatApiError(e));
      setQuietHours(!checked);
    }
  };

  const saveProfile = async () => {
    try { await api.patch("/profile", { name, title }); toast.success("Profile updated"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Avatar updated"); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try { await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password }); toast.success("Password updated"); setPw({ current_password: "", new_password: "", confirm: "" }); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  let avatarSrc = null;
  if (user.avatar) {
    avatarSrc = user.avatar.startsWith("/api/")
      ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar}`
      : user.avatar;
  }

  return (
    <div className="p-4 sm:p-8 max-w-3xl mx-auto fade-in" data-testid="profile-page">
      <PageHeader eyebrow="Account" title="Your profile" subtitle="Update your photo, display name and password." />

      <MyAssetsStrip />

      <div className="card-flat p-6 mb-6">
        <div className="flex items-center gap-6 mb-6">
          <div className="relative">
            <div className="w-20 h-20 rounded-sm bg-slate-100 overflow-hidden flex items-center justify-center font-display text-3xl">
              {avatarSrc ? <img src={avatarSrc} alt="" className="w-full h-full object-cover" /> : (user.name || "?").slice(0, 1)}
            </div>
            <button onClick={() => fileRef.current?.click()} className="absolute -bottom-1 -right-1 bg-[#F54800] hover:bg-[#D63E00] text-white p-1.5 rounded-full" data-testid="avatar-upload-btn">
              <Camera size={12} />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onAvatar} data-testid="avatar-input" />
          </div>
          <div className="flex-1 space-y-3">
            <div><Label className="label-overline">Display name</Label><Input className="rounded-sm mt-1" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" /></div>
            <div><Label className="label-overline">Title</Label><Input className="rounded-sm mt-1" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="profile-title" /></div>
            <Button onClick={saveProfile} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="save-profile">Save profile</Button>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-sm pt-4 border-t border-slate-100">
          <div><div className="label-overline">Email</div><div className="mt-1 font-mono text-xs">{user.email}</div></div>
          <div><div className="label-overline">Role</div><div className="mt-1 capitalize">{user.role.replace(/_/g, " ")}</div></div>
          <div><div className="label-overline">BU</div><div className="mt-1">{user.business_unit || "—"}</div></div>
        </div>
      </div>

      <form onSubmit={submit} className="card-flat p-6">
        <div className="font-display text-lg font-bold mb-4">Change password</div>
        <div className="space-y-3">
          <div><Label className="label-overline">Current</Label><Input type="password" className="rounded-sm mt-1" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required /></div>
          <div><Label className="label-overline">New</Label><Input type="password" className="rounded-sm mt-1" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required minLength={6} /></div>
          <div><Label className="label-overline">Confirm</Label><Input type="password" className="rounded-sm mt-1" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required minLength={6} /></div>
        </div>
        <Button disabled={saving} type="submit" data-testid="change-password-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white mt-4">{saving ? "Saving…" : "Update password"}</Button>
      </form>

      <div className="card-flat p-6 mt-6" data-testid="notification-preferences-card">
        <div className="font-display text-lg font-bold mb-1 inline-flex items-center gap-2"><Bell size={16} className="text-[#F54800]" /> Notification preferences</div>
        <p className="text-sm text-slate-500 mb-4">In-app notifications always show up in your bell. Choose which categories also email you.</p>
        <div className="space-y-3">
          {prefGroups.map((g) => (
            <div key={g.key} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0" data-testid={`notif-pref-row-${g.key}`}>
              <span className="text-sm text-slate-700">{g.label}</span>
              <Switch checked={g.email_enabled} onCheckedChange={(v) => toggleGroup(g.key, v)} data-testid={`notif-pref-toggle-${g.key}`} />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between py-3 mt-2 border-t border-slate-200" data-testid="quiet-hours-row">
          <div>
            <span className="text-sm text-slate-700 font-medium">Quiet hours</span>
            <p className="text-xs text-slate-400">Mute emails 9 PM – 9 AM IST + weekends — queued and delivered once quiet hours end. In-app notifications are never affected.</p>
          </div>
          <Switch checked={quietHours} onCheckedChange={toggleQuietHours} data-testid="quiet-hours-toggle" />
        </div>
      </div>
    </div>
  );
}
